import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Edit2,
  ExternalLink,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

import useSEO from "../../lib/useSEO";
import {
  adminDeleteProduct,
  adminListProducts,
  formatApiError,
  imageUrl,
} from "../../lib/api";
import { FORMAT_IDR } from "../../lib/constants";

export default function AdminProductsList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useSEO({ title: "Kelola Produk — Admin Ace Sublimasi" });

  const load = () => {
    setLoading(true);
    adminListProducts()
      .then(setProducts)
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const doDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await adminDeleteProduct(toDelete.id);
      toast.success(`Produk "${toDelete.name}" terhapus`);
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Gagal menghapus produk"
      );
    } finally {
      setDeleting(false);
    }
  };

  const filtered = products.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      p.name.toLowerCase().includes(q) ||
      p.slug.toLowerCase().includes(q) ||
      p.category_name.toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-products-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-2">
            / Products
          </p>
          <h1 className="font-display text-3xl sm:text-4xl uppercase text-white leading-none">
            Kelola Produk
          </h1>
          <p className="mt-2 text-sm text-[#A3A3A3]">
            {loading ? "Memuat..." : `${products.length} produk aktif di website.`}
          </p>
        </div>
        <Link
          to="/admin/products/new"
          data-testid="admin-add-product-btn"
          className="btn-primary !py-3"
        >
          <Plus className="w-4 h-4" />
          Tambah Produk
        </Link>
      </div>

      <div className="flex border border-[#262626] focus-within:border-[#CCFF00] transition-colors max-w-md">
        <span className="px-3 flex items-center text-[#A3A3A3]">
          <Search className="w-4 h-4" />
        </span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari nama, slug, atau kategori..."
          data-testid="admin-products-search"
          className="flex-1 bg-transparent text-sm text-white py-3 outline-none pr-3"
        />
      </div>

      {loading ? (
        <div className="grid gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-20 border border-[#262626] bg-[#0a0a0a] animate-pulse"
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="admin-products-empty"
          className="border border-[#262626] p-10 text-center"
        >
          <p className="font-display text-xl uppercase text-white">
            Belum ada produk
          </p>
          <p className="mt-2 text-sm text-[#A3A3A3]">
            Klik “Tambah Produk” untuk menambahkan produk pertama.
          </p>
        </div>
      ) : (
        <div
          data-testid="admin-products-table"
          className="border border-[#262626] overflow-hidden"
        >
          <div className="hidden md:grid md:grid-cols-[80px_1fr_140px_140px_160px] gap-4 px-4 py-3 border-b border-[#262626] bg-[#0a0a0a] text-[10px] font-bold tracking-[0.25em] uppercase text-[#CCFF00]">
            <span>Foto</span>
            <span>Nama / Slug</span>
            <span>Kategori</span>
            <span>Harga (Rp)</span>
            <span className="text-right">Aksi</span>
          </div>
          <div className="divide-y divide-[#262626]">
            {filtered.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-row-${p.slug}`}
                className="grid grid-cols-[80px_1fr] md:grid-cols-[80px_1fr_140px_140px_160px] gap-4 px-4 py-4 items-center"
              >
                <div className="w-16 h-20 md:w-14 md:h-14 border border-[#262626] bg-[#0a0a0a] overflow-hidden">
                  {p.images?.length > 0 ? (
                    <img
                      src={imageUrl(p.images[0].url)}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-[#A3A3A3]">
                      no img
                    </div>
                  )}
                </div>

                <div className="min-w-0">
                  <p className="text-white font-bold truncate">{p.name}</p>
                  <p className="text-xs text-[#A3A3A3] truncate">/{p.slug}</p>
                  <div className="md:hidden mt-2 flex flex-col gap-1 text-xs text-[#A3A3A3]">
                    <span>{p.category_name}</span>
                    <span className="font-display text-base text-[#CCFF00]">
                      {FORMAT_IDR(p.price_from)}
                    </span>
                  </div>
                </div>

                <div className="hidden md:block text-xs text-[#A3A3A3]">
                  {p.category_name}
                </div>

                <div className="hidden md:block font-display text-lg text-[#CCFF00]">
                  {FORMAT_IDR(p.price_from)}
                </div>

                <div className="md:col-span-1 col-span-2 flex items-center gap-2 md:justify-end mt-2 md:mt-0 flex-wrap">
                  <Link
                    to={`/shop/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`admin-row-view-${p.slug}`}
                    className="w-9 h-9 border border-[#262626] text-white hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center justify-center transition-colors"
                    aria-label="Lihat di website"
                    title="Lihat di website"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                  <Link
                    to={`/admin/products/${p.id}`}
                    data-testid={`admin-row-edit-${p.slug}`}
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest bg-[#CCFF00] text-black px-3 py-2 hover:bg-[#AACC00]"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Edit
                  </Link>
                  <button
                    onClick={() => setToDelete(p)}
                    data-testid={`admin-row-delete-${p.slug}`}
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest border border-[#262626] text-white hover:border-[#FF3B30] hover:text-[#FF3B30] px-3 py-2 transition-colors"
                    aria-label={`hapus ${p.name}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Hapus
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent
          data-testid="delete-confirm-dialog"
          className="bg-[#0a0a0a] border border-[#262626] rounded-none"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase text-white text-2xl flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
              Hapus Produk?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A3A3A3]">
              Produk <strong className="text-white">{toDelete?.name}</strong> akan
              dihapus permanen dari website. Aksi ini tidak bisa dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel"
              className="rounded-none bg-transparent border border-[#262626] text-white hover:bg-[#0e0e0e] hover:text-white"
            >
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              onClick={doDelete}
              disabled={deleting}
              className="rounded-none bg-[#FF3B30] text-white hover:bg-[#d8312a]"
            >
              {deleting ? "Menghapus..." : "Hapus Permanen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
